import { faDashboard } from '@fortawesome/free-solid-svg-icons/faDashboard';
import { faDoorOpen } from '@fortawesome/free-solid-svg-icons/faDoorOpen';
import { faPeopleGroup } from '@fortawesome/free-solid-svg-icons/faPeopleGroup';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons/faShieldHalved';
import { faUserShield } from '@fortawesome/free-solid-svg-icons/faUserShield';
import { useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import React, { useContext } from 'react';

import { useAuth } from '../../../../../context/AuthContext';
import { UserRole } from '../../../../../types/user';
import { getIDTokenData } from '../../../../../utils/tokenUtils';
import { MenuContext } from '../MenuContext';
import { useAccountMenuStyles } from '../menuStyles/accountMenuStyles';
import SignUp from '../SignUp';
import HeaderEnum from '../topLevelMenu/headerEnum';
import { TopLevelMenu } from '../topLevelMenu/TopLevelMenu';

import { Account } from './Constants';
import { HeaderMenuItem } from './HeaderMenuItem';
import { getIcon } from './IconUtils';
import { MenuSection } from './MenuSection';

// HA-287: Roles allowed to see "Account Management" in the
// header menu. Same set as the practitionerOnly() helper in
// app.tsx — Clients can't manage practice users, so the link
// is dead-ended for them (HA-259 already redirects to /Dashboard
// at the route layer; HA-287 removes the dead link in the UI).
const PRACTITIONER_LIKE_ROLES: ReadonlyArray<UserRole> = [
  UserRole.Practitioner,
  UserRole.PracticeAdmin,
  UserRole.SystemAdmin,
];

export function AccountMenu() {
  const menuContext = useContext(MenuContext);
  const theme = useTheme();
  const styles = useAccountMenuStyles(theme, menuContext.selectedTheme);
  const idToken = getIDTokenData();
  const { currentUser } = useAuth();

  if (JSON.stringify(idToken).length <= 2) {
    return <SignUp />;
  }

  // HA-287: fail-closed — undefined currentUser (mid-bootstrap)
  // treated as non-practitioner so the link doesn't flash
  // before AuthContext resolves.
  const showAccountManagement = !!currentUser && PRACTITIONER_LIKE_ROLES.includes(currentUser.role);
  // HA-446: the admin console, for SystemAdmins only (same fail-closed rule).
  const showAdminConsole = currentUser?.role === UserRole.SystemAdmin;

  const userFullName = idToken?.email ?? menuContext.menuConfig?.UserFullName ?? '';
  const avatarText = menuContext.menuConfig?.UserInitials ?? '';

  return (
    <TopLevelMenu
      alternateTitle={HeaderEnum.Account}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      className="profile-menu"
      href={Account[HeaderEnum.Account]}
      icon={<Avatar className={styles.avatar}>{avatarText}</Avatar>}
      menuListClassName={`profile-menu-list ${styles.menuList}`}
      titleEnum={HeaderEnum.Account}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <MenuSection className={styles.userName}>{userFullName}</MenuSection>
      <HeaderMenuItem
        className={styles.menuItem}
        icon={getIcon(faDashboard)}
        relativePath={Account[HeaderEnum.Dashboard]}
        title={HeaderEnum.Dashboard}
      />
      {showAccountManagement && (
        <HeaderMenuItem
          className={styles.menuItem}
          icon={getIcon(faPeopleGroup)}
          relativePath={Account[HeaderEnum.AccountManagement]}
          title={HeaderEnum.AccountManagement}
        />
      )}
      {showAdminConsole && (
        <HeaderMenuItem
          className={styles.menuItem}
          icon={getIcon(faUserShield)}
          relativePath={Account[HeaderEnum.AdminConsole]}
          title={HeaderEnum.AdminConsole}
        />
      )}
      <HeaderMenuItem
        className={styles.menuItem}
        icon={getIcon(faShieldHalved)}
        relativePath={Account[HeaderEnum.TwoFactorAuthentication]}
        title={HeaderEnum.TwoFactorAuthentication}
      />
      <HeaderMenuItem
        className={styles.menuItem}
        icon={getIcon(faDoorOpen)}
        relativePath={Account[HeaderEnum.SignOut]}
        title={HeaderEnum.SignOut}
      />
    </TopLevelMenu>
  );
}
